import React, { useContext, useMemo } from 'react';
import { GeoJSONLayer } from 'components/MapboxMap';
import { featureCollection } from '@turf/helpers';
import { cloneDeep, isEmpty } from 'lodash';

import { Context } from 'components/Store';
import {
  calculateMapPDFScale,
  getCentroidGeoJSON
} from 'screens/Property/helpers/mapApiHelpers';
import {
  conditionFieldWithCropZones,
  customSymbolLayout,
  customSymbolPaint,
  legendSymbolLayout
} from '../../../PropertyMap/utilities/mapboxLayerHelpers';

export const CustomLayers = ({
  geoJSONArr,
  stylesArr,
  onClickShape,
  isMultipleSelect,
  showLegendLabels
}) => {
  const [{ isExportPDFMapsActive, exportPDFMapsStep }] = useContext(Context);

  // Derived once per input change, not per render: the map layer reloads its
  // source whenever it is handed a new data object, and every state update
  // after a drag re-rendered this, so the shapes and labels pulsed once per
  // update. Same objects in, same objects out, no reload.
  const layerData = useMemo(
    () =>
      geoJSONArr.map(features => {
        const uniqueGeometries = new Set();

        let sortedFeatures = features
          .sort((a, b) => {
            if (!a.properties.sortKey) return -1;
            if (!b.properties.sortKey) return 1;
            return b.properties.sortKey - a.properties.sortKey;
          })
          .map(feature => {
            const _feature = feature;
            _feature.properties.symbolSortKey =
              features.length - feature.properties.sortKey;
            return _feature;
          });

        // In export preview, remove labels from overlapping crop zones
        if (isExportPDFMapsActive && exportPDFMapsStep === 1) {
          sortedFeatures = sortedFeatures
            // Reverse the array so that the highest sortKey feature keeps its label
            .reverse()
            // Remove labels from crop zones with the same geometry as a previous crop zone
            .map(feature => {
              if (feature.properties.$landType !== 'cropzone') return feature;

              const geometryString = JSON.stringify(feature.geometry);
              const newFeature = cloneDeep(feature);
              if (uniqueGeometries.has(geometryString)) {
                newFeature.properties.customLabel = undefined;
                newFeature.properties.labelLegendKey = undefined;
              } else {
                uniqueGeometries.add(geometryString);
              }
              return newFeature;
            })
            // Reverse the array back to its original order
            .reverse();
        }

        const fc = featureCollection(sortedFeatures);
        return { fc, centroids: getCentroidGeoJSON(fc) };
      }),
    [geoJSONArr, isExportPDFMapsActive, exportPDFMapsStep]
  );

  return geoJSONArr.map((features, idx) => {
    if (!stylesArr[idx] || isEmpty(stylesArr[idx])) return null;

    const { fc, centroids } = layerData[idx];
    const { fillColor, fillOutlineColor, lineWidth } = stylesArr[idx];

    const fillPaint = isMultipleSelect
      ? {
          'fill-color': [
            'case',
            conditionFieldWithCropZones,
            'rgba(20, 21, 28, 0.3)',
            ['has', 'cropColor'],
            ['get', 'cropColor'],
            fillColor
          ]
        }
      : {
          'fill-color': fillColor,
          'fill-outline-color': fillOutlineColor
        };

    let linePaint = {};
    if (isMultipleSelect) {
      linePaint = {
        'line-color': [
          'case',
          ['has', 'cropColor'],
          'transparent',
          conditionFieldWithCropZones,
          '#14151C',
          fillOutlineColor
        ],
        'line-width': ['case', ['has', 'cropColor'], 0, 2]
      };
    } else if (lineWidth) {
      linePaint = { 'line-color': fillOutlineColor, 'line-width': lineWidth };
    }

    // A copy: the shared layout would keep the export size after the export
    // ends, and the map layer only picks up a change through a new object.
    const symbolLayout = {
      ...(showLegendLabels ? legendSymbolLayout : customSymbolLayout)
    };

    if (isExportPDFMapsActive) {
      symbolLayout['text-size'] = exportPDFMapsStep === 0 ? 14 : 10;
      if (exportPDFMapsStep === 1) {
        symbolLayout['text-size'] *= calculateMapPDFScale();
      }
    }

    return (
      // Every custom-layer feature sits in every style group, so a feature id
      // alone can repeat across groups; the group index keeps the keys apart.
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={`${idx}-${features[0]?.properties?.id}`}>
        <GeoJSONLayer data={fc} linePaint={linePaint} />
        <GeoJSONLayer
          data={fc}
          fillOnClick={onClickShape}
          fillPaint={{
            ...fillPaint,
            'fill-antialias': true
          }}
        />
        <GeoJSONLayer
          data={centroids}
          symbolLayout={symbolLayout}
          symbolPaint={customSymbolPaint}
        />
      </React.Fragment>
    );
  });
};

export default CustomLayers;
